<script setup lang="ts">
import BaseImage from '@/components/ui/BaseImage.vue'
import { computed } from 'vue'
import { formatDate } from '@/utils/stringFormatting'
import type { Conversation, ImageFocus } from '@/types/domain'

type RecentConversation = Conversation & {
  character_name?: string | null
  character_img?: string | null
  character_img_focus?: ImageFocus | null
}

const props = defineProps<{
  conversation: RecentConversation
}>()

const emit = defineEmits<{
  click: [conversation: RecentConversation]
}>()

const displayTitle = computed(() => props.conversation.title || props.conversation.character_name || 'Untitled')
const preview = computed(() => props.conversation.last_message_preview || '')
const timestamp = computed(() => {
  const date = props.conversation.last_message_at || props.conversation.created_at
  return date ? formatDate(date) : ''
})
</script>

<template>
  <button
    type="button"
    class="recent-story-card"
    :title="displayTitle"
    @click="emit('click', props.conversation)"
  >
    <div class="recent-story-card__portrait-wrap">
      <BaseImage
        class="recent-story-card__portrait"
        :src="props.conversation.character_img"
        :alt="props.conversation.character_name || 'Character'"
        :image-focus="props.conversation.character_img_focus"
      />
      <span
        v-if="conversation.message_count != null"
        class="recent-story-card__msg-badge"
        :title="`${conversation.message_count} messages`"
      >{{ conversation.message_count }}</span>
    </div>
    <div class="recent-story-card__body">
      <p class="recent-story-card__title">{{ displayTitle }}</p>
      <p v-if="preview" class="recent-story-card__preview">{{ preview }}</p>
      <p v-if="timestamp" class="recent-story-card__timestamp">{{ timestamp }}</p>
    </div>
  </button>
</template>

<style scoped>
.recent-story-card {
  display: flex;
  flex-direction: column;
  width: 160px;
  flex-shrink: 0;
  background: var(--theme-surface);
  border: 1px solid var(--theme-border);
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  text-align: left;
  transition: border-color 150ms, transform 150ms, box-shadow 150ms;
  padding: 0;
}

.recent-story-card:hover {
  border-color: var(--theme-border-hover);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

.recent-story-card:active {
  transform: translateY(0);
}

.recent-story-card__portrait-wrap {
  position: relative;
  width: 100%;
  aspect-ratio: 3/2;
  overflow: hidden;
}

.recent-story-card__msg-badge {
  position: absolute;
  bottom: 6px;
  right: 6px;
  background: rgba(0, 0, 0, 0.55);
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.62rem;
  font-weight: 600;
  line-height: 1;
  padding: 3px 5px;
  border-radius: 4px;
  backdrop-filter: blur(4px);
  pointer-events: none;
}

.recent-story-card__portrait {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 200ms;
}

.recent-story-card:hover .recent-story-card__portrait {
  transform: scale(1.04);
}

.recent-story-card__body {
  padding: 10px 11px 11px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 0;
}

.recent-story-card__title {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--theme-on-surface);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.recent-story-card__preview {
  font-size: 0.72rem;
  line-height: 1.4;
  color: var(--theme-text-tertiary);
  font-style: italic;
  margin: 0;
  /* 1 line clamp */
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.recent-story-card__timestamp {
  font-size: 0.68rem;
  color: var(--theme-text-faint);
  margin: 0;
}
</style>
